import { Component, EventEmitter, Input, OnInit, OnChanges, OnDestroy, Output, SimpleChanges } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { Subscription } from "rxjs";
import { TitleCasePipe } from '@angular/common';
import { ADDRESS_OWNERSHIP, ADDRESS_TYPES } from "src/app/constants/contact";
import { SEARCH_DEBOUNCE_MILLIS } from 'src/app/constants/list'
import { DBL_CLICK_TIMEOUT } from "src/app/constants/edit";
import { AddressType } from "src/app/models/address-type.model";
import { Address } from "src/app/models/address.model";
import { MasterService } from "src/app/services/master.service";
import { MessageService } from "src/app/services/message.service";
import { addyIcon } from "src/app/utils/icons";

@Component({
    selector: 'addresses-compact',
    templateUrl: './addresses-compact.component.html',
    styleUrls: ['./addresses-compact.component.scss'],
    providers: [TitleCasePipe],
    standalone: false
})

export class AddressesCompactComponent implements OnInit, OnChanges, OnDestroy {

    /** */
    @Input() contactId: number | null = null

    /** */
    @Input() language: string = 'EN'

    /** */
    @Input() words = {}

    /** */
    @Output() modified: EventEmitter<Object> = new EventEmitter()

    /** A list of addresses associated with the current contact. */
    protected addresses: Address[] = []

    /** A form editor for addresses. */
    protected addressEdit: FormGroup | null = null

    /** Available communication types. */
    protected addressTypes: AddressType[] = ADDRESS_TYPES
    
    /** Available communication subtypes. */
    protected addressOwnerships: string[] = ADDRESS_OWNERSHIP

    /** A debounce timer for click events. Used to wait for the user to click twice before performing an action. */
    private dblClickWatcher: ReturnType<typeof setTimeout> = null

    /** A debounce timer for zip code searches. Used to wait for the user to finish typing before performing an action. */
    private watcher: ReturnType<typeof setTimeout> = null

    private subscriptions: Subscription[] = []

    get location() {
        const city = this.addressEdit.get('city').value
        if (!city || (city.trim().length <= 0)) {
            return ''
        }

        const state = this.addressEdit.get('state').value
        const county = this.addressEdit.get('county').value

        return `${this.titleCasePipe.transform(city)}, ${state.toUpperCase()} (${this.titleCasePipe.transform(county)} county)`
    } 

    constructor(private master: MasterService, private ms: MessageService, private fb: FormBuilder, private titleCasePipe: TitleCasePipe) {

        this.addressEdit = this.fb.group({
            id: new FormControl<number>(0, [Validators.required]),
            contactId: new FormControl<number>(0, [Validators.required]),
            addressTypeId: new FormControl<number | null>(null, [Validators.required]),
            status: new FormControl<string | null>(null),
            address: new FormControl<string | null>(null, [Validators.required]),
            address2: new FormControl<string | null>(null),
            zipCode: new FormControl<string | null>(null, [Validators.required, Validators.pattern('[0-9]{3,10}')]),
            city: new FormControl<string | null>(null, [Validators.required]),
            state: new FormControl<string | null>(null, [Validators.required]),
            county: new FormControl<string | null>(null, [Validators.required]),
            country: new FormControl<string>('United States', [Validators.required]),
            primaryAddress: new FormControl<boolean>(false)
        })

    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.addressEdit.get('zipCode').valueChanges.subscribe((code) => {
            if (!code || code.length < 5) {
                this.clearLocation()
                return
            }

            clearTimeout(this.watcher)

            this.watcher = setTimeout(() => {
                this.clearLocation()

                this.master.get(`readLocationByZipcode?zipcode=${code}`, res => {
                    if (!res) {
                        this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                        return
                    }

                    if (res.status !== 200) {
                        return
                    }

                    const found = res.data.locations
                    if ((found) && (found.length === 1)) {
                        this.setLocation(found[0])
                    }
                })
            }, SEARCH_DEBOUNCE_MILLIS)
        })
    )}

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes) {
            return
        }

        if (changes.contactId) {
            this.fetch()
        }
    }

    ngOnDestroy(): void {
        for (const sub of this.subscriptions) {
            sub.unsubscribe()
        }
        if (this.watcher) {
            clearTimeout(this.watcher)
        }
        if (this.dblClickWatcher) {
            clearTimeout(this.dblClickWatcher)
        }
    }

    /**
     * Reset the city, state and county inputs to empty during a search.
     */
    private clearLocation = () => {
        this.addressEdit.get('city').setValue('')
        this.addressEdit.get('state').setValue('')
        this.addressEdit.get('county').setValue('')
    }

    /**
     * Apply the found location information to the input form for display.
     */
    private setLocation = (location: Address) => {
        if (!location) {
            return
        }

        this.addressEdit.get('city').setValue(location.city)
        this.addressEdit.get('state').setValue(location.state)
        this.addressEdit.get('county').setValue(location.county)
    }

    /**
     * 
     */
    private fetch = (): void => {        
        if (!this.contactId || (this.contactId <= 0)) {
            return
        }
        
        this.master.get(`contacts/${this.contactId}/addresses`, (res) => {  
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }
  
            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            if (!res.data.addresses) {
                return
            }
            
            this.addresses = res.data.addresses
        })
    }

    /**
     * Given an address type return a material icon to display.
     * @param type An address type to map to an icon.
     */
    protected addyIcon = (type: string): string => {
        return addyIcon(type)
    }

    /**
     * 
     * @param e 
     */
    protected addAddress = (e: Event): void => {
        this.cancelAddress(e)

        if (!this.addresses) {
            this.addresses = []
        }

        const addy = new Address()
        this.addressEdit.patchValue({
            id: 0,
            contactId: this.contactId,
            addressTypeId: 2, // Home
            status: 'rent',
            country: 'United States',
            primaryAddress: false
        })

        this.addresses.push({
            ...addy,
            id: 0,
            country: 'United States',
            primaryAddress: false,
            editing: true
        })
    }

    /**
     * 
     * @param e 
     * @param index
     */
    protected editAddress = (e: Event, index: number): void => {
        this.cancelAddress(e)
        
        if (!this.addresses || (this.addresses.length <= 0)) {
            return
        }
        
        if (this.dblClickWatcher !== null) {
            const addy = this.addresses[index]
            this.addressEdit.patchValue({
                ...addy,
                addressTypeId: addy.addressType.id,
                status: (addy.status || 'own').toLocaleLowerCase()
            }, {emitEvent: false})

            addy.editing = true
        }

        const ctx = this
        this.dblClickWatcher = setTimeout(() => {
            ctx.dblClickWatcher = null
        }, DBL_CLICK_TIMEOUT)
    }

    /**
     * 
     * @param e 
     */
    protected cancelAddress = (e: Event) => {
        e.stopPropagation()
        e.preventDefault()

        this.addressEdit.reset()
        const index = this.addresses.findIndex((item) => item.editing)
        if (index < 0) {
            return
        }

        const addy = this.addresses[index]
        if (addy.id && (addy.id > 0)) {
            addy.editing = false
            return
        }

        this.addresses.splice(index, 1)
    }

    /**
     * 
     * @param e 
     */
    protected saveAddress = (e: Event) => {
        e.stopPropagation()
        e.preventDefault()

        const index = this.addresses.findIndex((item) => item.editing)
        if (index < 0) {
            return
        }

        const addy = Object.assign(this.addresses[index], this.addressEdit.value)
        const type = this.addressTypes.find((addyType) => addyType.id == addy.addressTypeId)
        addy.addressType = type

        if (addy.id && (addy.id > 0)) {
            this.modifyAddress(addy)
            return
        }

        this.insertAddress(addy)
    }

    /**
     * 
     * @param addy 
     */
    private insertAddress = (addy: Address) => {
        if (!addy) {
            return
        }

        const payload = {
            addresses: [
                JSON.parse(JSON.stringify(addy))
            ]
        }

        delete(payload.addresses[0].id)
        delete(payload.addresses[0].addressType)

        this.master.post(`contacts/${this.contactId}/addresses`, payload, (res) => {  
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }
  
            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            addy.editing = false
            this.addressEdit.reset()

            if (!res.data || !res.data.identities || (res.data.identities.length <= 0)) {
                return
            }
            
            addy.id = parseInt(res.data.identities[0])
            this.modified.emit({addresses: [{id: addy.id}]})
        })
    }

    /**
     * 
     * @param addy 
     */
    private modifyAddress = (addy: Address) => {
        if (!addy || !addy.id) {
            return
        }

        const payload = JSON.parse(JSON.stringify(addy))
        
        delete(payload.id)
        delete(payload.addressType)

        this.master.put(`contacts/${this.contactId}/addresses/${addy.id}`, {address: payload}, (res) => {
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }
  
            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }
            
            addy.editing = false
            this.addressEdit.reset()
            this.modified.emit({addresses: [{id: addy.id}]})
        })
    }

    /**
     * 
     * @param e 
     * @param index
     */
    protected discardAddress = (e: Event, index: number): void => {
        if (!this.addresses || (this.addresses.length <= 0) || (index < 0) || (index >= this.addresses.length)) {
            return
        }

        const addy = this.addresses[index]
        const addyId = addy.id

        this.master.discard(`contacts/${this.contactId}/addresses/${addyId}`, (res) => {  
            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }
  
            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }
            
            this.addresses.splice(index, 1)
            this.modified.emit({addresses: [{id: addy.id}]})
        })
    }
}